import { useState, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, XCircle, RefreshCw, ChevronRight, Lightbulb } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { getApiErrorMessage } from '../../utils/apiError';
import useReducedMotion from '../../hooks/useReducedMotion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


/**
 * Extracts fill-in-blank questions from various backend shapes.
 * @param {Object} lesson
 * @returns {Array|null}
 */
function getQuestions(lesson) {
  const raw =
    lesson?.content?.questions ||
    lesson?.questions ||
    lesson?.content?.fill_in_blank ||
    null;

  if (raw && Array.isArray(raw) && raw.length > 0) {
    return raw.map((q, i) => ({
      id: q.id || i + 1,
      sentence: q.sentence || q.text || '',
      answers: Array.isArray(q.answers) ? q.answers : [],
      correct: q.correct || q.correct_answer || '',
      mode: q.mode || (q.answers?.length > 0 ? 'word_pool' : 'text_input'),
      hint: q.hint || '',
    }));
  }
  return null;
}

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/**
 * FillInBlankLesson — Bosłuk doldurma alıştırması (lesson_type === 'fill_in_blank').
 *
 * Mode A (word_pool): kelime havuzundan tıkla boşluğa yerleştir.
 * Mode B (text_input): doğrudan yaz, "Kontrol Et" → doğru/yanlış.
 *
 * Backend content shape (Faz 4e ile gelecek):
 *   lesson.content.questions: [{ sentence, answers, correct, mode, hint }]
 *
 * @param {{ lesson: Object, courseId: string, onComplete: Function, completedLessonIds: string[] }} props
 */
export default function FillInBlankLesson({ lesson, courseId, onComplete, completedLessonIds = [] }) {
  const { t, lang } = useLanguage();
  const { authHeaders } = useAuth();
  const toast = useToast();
  const reduced = useReducedMotion();

  const lessonId = lesson?.lesson_id;
  const isCompleted = completedLessonIds.includes(lessonId);

  const rawQs = getQuestions(lesson);
  const hasContent = rawQs !== null && rawQs.length > 0;
  // Stabilize questions: without useMemo, `hasContent ? rawQs : []` creates a
  // new array ref when rawQs is null, invalidating handleCheck/handleNext.
  const questions = useMemo(() => hasContent ? rawQs : [], [hasContent, rawQs]);
  const total = questions.length;

  const [qIndex, setQIndex] = useState(0);
  const [selected, setSelected] = useState(''); // current answer (word pool selection or text input)
  const [checked, setChecked] = useState(false);
  const [isCorrect, setIsCorrect] = useState(null);
  const [score, setScore] = useState(0);
  const [finished, setFinished] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [shuffledPool, setShuffledPool] = useState(() => shuffle(questions[0]?.answers || []));

  const current = questions[qIndex];

  const handleWordSelect = useCallback((word) => {
    if (checked) return;
    setSelected(word);
  }, [checked]);

  const handleCheck = useCallback(() => {
    if (!selected.trim() || checked) return;
    // Read current question from questions[qIndex] inside callback to avoid
    // capturing a stale `current` object reference in the dep array.
    const q = questions[qIndex];
    if (!q) return;
    const correct = selected.trim().toLowerCase() === q.correct.trim().toLowerCase();
    setIsCorrect(correct);
    setChecked(true);
    if (correct) setScore(s => s + 1);
  }, [selected, checked, questions, qIndex]);

  const handleNext = useCallback(() => {
    const nextIdx = qIndex + 1;
    if (nextIdx >= total) {
      setFinished(true);
    } else {
      setQIndex(nextIdx);
      setSelected('');
      setChecked(false);
      setIsCorrect(null);
      setShowHint(false);
      setShuffledPool(shuffle(questions[nextIdx]?.answers || []));
    }
  }, [qIndex, total, questions]);

  const handleRetry = () => {
    setSelected('');
    setChecked(false);
    setIsCorrect(null);
    setShowHint(false);
  };

  const restart = () => {
    setQIndex(0);
    setSelected('');
    setChecked(false);
    setIsCorrect(null);
    setScore(0);
    setFinished(false);
    setShowHint(false);
    setShuffledPool(shuffle(questions[0]?.answers || []));
  };

  const handleComplete = async () => {
    if (completing) return;
    setCompleting(true);
    const finalScore = total > 0 ? Math.round((score / total) * 100) : 0;
    try {
      await axios.post(
        `${API}/enrollments/complete-lesson`,
        { course_id: courseId, lesson_id: lessonId, quiz_score: finalScore },
        { headers: authHeaders() }
      );
      toast.success(lang === 'tr' ? 'Ders tamamlandı!' : 'Lesson completed!');
      if (onComplete) onComplete();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setCompleting(false);
    }
  };

  if (isCompleted) {
    return (
      <div className="rounded-2xl border-2 border-green-200 bg-green-50 p-6 mb-6 text-center" data-testid="fillinblank-already-completed">
        <CheckCircle size={32} className="mx-auto text-green-600 mb-2" />
        <p className="font-bold text-sm text-green-700">
          {lang === 'tr' ? 'Bu ders tamamlandı' : 'This lesson is completed'}
        </p>
      </div>
    );
  }

  if (!hasContent) {
    return (
      <div className="text-center py-12" data-testid="fillinblank-empty-state">
        <CheckCircle size={40} className="mx-auto text-gray-300 mb-4" />
        <p className="text-gray-500 font-semibold mb-4">
          {lang === 'tr' ? 'Bu derste henüz boşluk doldurma içeriği tanımlanmamış.' : 'No fill-in-blank content has been defined for this lesson yet.'}
        </p>
        <motion.button
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          onClick={handleComplete}
          disabled={completing}
          className="px-6 py-3 rounded-xl border-2 border-emerald-500 bg-emerald-500 text-white font-bold shadow-[0_4px_0_#059669] active:shadow-none active:translate-y-1 transition-all disabled:opacity-50"
          data-testid="fillinblank-empty-complete-btn"
        >
          {completing ? '...' : t('common.completeLesson')}
        </motion.button>
      </div>
    );
  }

  return (
    <div data-testid="fill-in-blank-lesson">
      {/* Finished screen */}
      <AnimatePresence mode="wait">
        {finished ? (
          <motion.div
            key="fib-done"
            initial={reduced ? {} : { opacity: 0, scale: 0.92 }}
            animate={reduced ? {} : { opacity: 1, scale: 1 }}
            className="rounded-2xl border-2 border-cyan-200 bg-cyan-50 p-8 text-center"
            data-testid="fillinblank-completed"
          >
            <div className="w-20 h-20 rounded-full bg-cyan-500 flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={40} className="text-white" />
            </div>
            <h2 className="font-display text-3xl font-bold text-cyan-700 mb-1">
              {t('fillInBlankLesson.completed')}
            </h2>
            <p className="text-gray-600 font-semibold mb-6">
              {score}/{total} {t('fillInBlankLesson.correct')} — {t('fillInBlankLesson.score')}: %{Math.round((score / total) * 100)}
            </p>
            <div className="flex justify-center gap-4">
              <motion.button
                whileHover={reduced ? {} : { scale: 1.03 }}
                whileTap={reduced ? {} : { scale: 0.97 }}
                onClick={handleComplete}
                disabled={completing}
                className="px-8 py-3 rounded-xl border-2 border-emerald-500 bg-emerald-500 text-white font-bold shadow-[0_4px_0_#059669] active:shadow-none active:translate-y-1 transition-all disabled:opacity-50 flex items-center gap-2"
                data-testid="fillinblank-complete-btn"
              >
                <CheckCircle size={18} />
                {completing ? '...' : t('common.completeLesson')}
              </motion.button>
              <motion.button
                whileHover={reduced ? {} : { scale: 1.03 }}
                whileTap={reduced ? {} : { scale: 0.97 }}
                onClick={restart}
                className="px-6 py-3 rounded-xl border-2 border-gray-300 bg-white font-bold shadow-[0_4px_0_#d1d5db] active:shadow-none active:translate-y-1 transition-all flex items-center gap-2 text-gray-700"
                data-testid="fillinblank-restart-btn"
              >
                <RefreshCw size={16} /> {t('fillInBlankLesson.retry')}
              </motion.button>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key={`fib-q-${qIndex}`}
            initial={reduced ? {} : { opacity: 0, x: 20 }}
            animate={reduced ? {} : { opacity: 1, x: 0 }}
            exit={reduced ? {} : { opacity: 0, x: -20 }}
            transition={reduced ? {} : { duration: 0.25 }}
            data-testid="fillinblank-question"
          >
            {/* Progress */}
            <div className="mb-4">
              <div className="flex justify-between text-xs font-bold text-gray-500 mb-1">
                <span>{t('fillInBlankLesson.question')} {qIndex + 1}/{total}</span>
                <span className="text-emerald-600">{score} {t('fillInBlankLesson.correct')}</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden border border-gray-300">
                <motion.div
                  className="h-full bg-cyan-500 rounded-full"
                  animate={{ width: `${((qIndex) / total) * 100}%` }}
                  transition={reduced ? { duration: 0 } : { type: 'spring', stiffness: 100 }}
                />
              </div>
            </div>

            {/* Sentence with blank */}
            <SentenceDisplay
              sentence={current.sentence}
              filled={selected}
              checked={checked}
              isCorrect={isCorrect}
              correctAnswer={current.correct}
              mode={current.mode}
              reduced={reduced}
              t={t}
              lang={lang}
              onTextChange={setSelected}
            />

            {/* Hint */}
            {current.hint && (
              <div className="mt-3 mb-2">
                {!showHint ? (
                  <button
                    onClick={() => setShowHint(true)}
                    className="flex items-center gap-1.5 text-xs font-bold text-amber-600 hover:text-amber-700 transition-colors"
                    data-testid="fillinblank-hint-btn"
                  >
                    <Lightbulb size={14} /> {t('fillInBlankLesson.hint')}
                  </button>
                ) : (
                  <div className="rounded-xl border-2 border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700 font-semibold flex items-center gap-2" data-testid="fillinblank-hint-text">
                    <Lightbulb size={14} className="flex-shrink-0" /> {current.hint}
                  </div>
                )}
              </div>
            )}

            {/* Word pool */}
            {current.mode === 'word_pool' && !checked && (
              <div className="mt-4 flex flex-wrap gap-2" data-testid="fillinblank-word-pool">
                {shuffledPool.map((word, i) => (
                  <motion.button
                    key={i}
                    whileHover={reduced ? {} : { scale: 1.04 }}
                    whileTap={reduced ? {} : { scale: 0.96 }}
                    onClick={() => handleWordSelect(word)}
                    className={`px-4 py-2 rounded-xl border-2 font-bold text-sm transition-all ${
                      selected === word
                        ? 'border-cyan-500 bg-cyan-500 text-white shadow-[0_3px_0_#0891b2]'
                        : 'border-gray-300 bg-white text-gray-700 shadow-[0_3px_0_#d1d5db] hover:border-cyan-300'
                    } active:shadow-none active:translate-y-0.5`}
                    data-testid={`fillinblank-word-${i}`}
                  >
                    {word}
                  </motion.button>
                ))}
              </div>
            )}

            {/* Feedback */}
            {checked && (
              <AnimatePresence>
                <motion.div
                  initial={reduced ? {} : { opacity: 0, y: 8 }}
                  animate={reduced ? {} : { opacity: 1, y: 0 }}
                  className={`mt-4 rounded-xl border-2 px-4 py-3 font-semibold flex items-start gap-2 ${
                    isCorrect
                      ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                      : 'border-rose-300 bg-rose-50 text-rose-700'
                  }`}
                  data-testid="fillinblank-feedback"
                >
                  {isCorrect
                    ? <CheckCircle size={18} className="flex-shrink-0 mt-0.5" />
                    : <XCircle size={18} className="flex-shrink-0 mt-0.5" />
                  }
                  <div>
                    <span className="font-bold">
                      {isCorrect ? t('fillInBlankLesson.correct') : t('fillInBlankLesson.incorrect')}
                    </span>
                    {!isCorrect && (
                      <span className="ml-1 text-sm">
                        {t('fillInBlankLesson.correctAnswerLabel')}: <strong>{current.correct}</strong>
                      </span>
                    )}
                  </div>
                </motion.div>
              </AnimatePresence>
            )}

            {/* Action buttons */}
            <div className="mt-4 flex gap-3 justify-end">
              {!checked ? (
                <motion.button
                  whileHover={(!selected.trim() || reduced) ? {} : { scale: 1.03 }}
                  whileTap={(!selected.trim() || reduced) ? {} : { scale: 0.97 }}
                  onClick={handleCheck}
                  disabled={!selected.trim()}
                  className="px-6 py-3 rounded-xl border-2 border-cyan-500 bg-cyan-500 text-white font-bold shadow-[0_4px_0_#0891b2] active:shadow-none active:translate-y-1 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="fillinblank-check-btn"
                >
                  {t('fillInBlankLesson.check')}
                </motion.button>
              ) : (
                <div className="flex gap-3">
                  {!isCorrect && (
                    <motion.button
                      whileHover={reduced ? {} : { scale: 1.03 }}
                      whileTap={reduced ? {} : { scale: 0.97 }}
                      onClick={handleRetry}
                      className="px-5 py-3 rounded-xl border-2 border-gray-300 bg-white font-bold shadow-[0_3px_0_#d1d5db] active:shadow-none active:translate-y-0.5 transition-all text-gray-700 flex items-center gap-2"
                      data-testid="fillinblank-retry-btn"
                    >
                      <RefreshCw size={15} /> {t('fillInBlankLesson.retry')}
                    </motion.button>
                  )}
                  <motion.button
                    whileHover={reduced ? {} : { scale: 1.03 }}
                    whileTap={reduced ? {} : { scale: 0.97 }}
                    onClick={handleNext}
                    className="flex items-center gap-2 px-6 py-3 rounded-xl border-2 border-cyan-500 bg-cyan-500 text-white font-bold shadow-[0_4px_0_#0891b2] active:shadow-none active:translate-y-1 transition-all"
                    data-testid="fillinblank-next-btn"
                  >
                    {t('fillInBlankLesson.next')} <ChevronRight size={18} />
                  </motion.button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function SentenceDisplay({ sentence, filled, checked, isCorrect, correctAnswer, mode, reduced: _reduced, t: _t, lang, onTextChange }) {
  // Split sentence at ___ placeholder
  const parts = sentence.split('___');
  const displayValue = checked ? (isCorrect ? filled : correctAnswer) : filled;

  return (
    <div className="rounded-2xl border-2 border-gray-200 bg-white shadow-[0_4px_0_#e5e7eb] p-6" data-testid="fillinblank-sentence">
      <p className="font-display text-2xl font-bold text-gray-800 leading-relaxed flex flex-wrap items-center gap-x-1">
        {parts[0] && <span>{parts[0]}</span>}
        {/* Blank slot */}
        {mode === 'text_input' ? (
          <input
            type="text"
            value={checked ? displayValue : filled}
            onChange={e => onTextChange(e.target.value)}
            disabled={checked}
            aria-label={lang === 'tr' ? 'Boşluğu doldur' : 'Fill in the blank'}
            className={`inline-block min-w-[120px] max-w-[200px] px-3 py-1 rounded-xl border-2 font-bold text-center transition-colors outline-none text-xl ${
              !checked
                ? 'border-cyan-400 bg-cyan-50 text-cyan-700 focus:border-cyan-600 focus:ring-2 focus:ring-cyan-200'
                : isCorrect
                  ? 'border-emerald-400 bg-emerald-50 text-emerald-700'
                  : 'border-rose-400 bg-rose-50 text-rose-700'
            }`}
            data-testid="fillinblank-text-input"
          />
        ) : (
          <span
            className={`inline-block min-w-[120px] px-4 py-1 rounded-xl border-2 font-bold text-center transition-colors ${
              !filled
                ? 'border-dashed border-gray-300 text-gray-400 text-base'
                : checked
                  ? isCorrect
                    ? 'border-emerald-400 bg-emerald-50 text-emerald-700'
                    : 'border-rose-400 bg-rose-50 text-rose-700'
                  : 'border-cyan-400 bg-cyan-50 text-cyan-700'
            }`}
            data-testid="fillinblank-blank-slot"
          >
            {filled || '___'}
          </span>
        )}
        {parts[1] && <span>{parts[1]}</span>}
      </p>
    </div>
  );
}
